import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GitCompare, ArrowRight, ChevronRight, HelpCircle, Home, Sparkles } from 'lucide-react';
import SEO, { buildFAQSchema, buildArticleSchema, buildBreadcrumbSchema } from '../components/SEO';
import { COMPARISON_PAGES, SITE_URL, BRAND } from '../lib/seoContent';

/**
 * Generic renderer for all Comparison pages listed in seoContent.COMPARISON_PAGES.
 * Adds a rich comparison-table + FAQ block for LLM citation.
 */
const ComparisonPage = ({ slug: slugProp }) => {
  const params = useParams();
  const slug = slugProp || params.slug;
  const article = COMPARISON_PAGES[slug];

  if (!article) {
    return (
      <div className="min-h-screen bg-[#F7F8F5] flex items-center justify-center px-6" data-testid="comparison-not-found">
        <div className="text-center max-w-md">
          <GitCompare className="h-12 w-12 text-[#004534] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#004534] mb-3">Comparison not found</h1>
          <Link to="/" className="text-[#D4AF37] font-semibold hover:underline">Return home →</Link>
        </div>
      </div>
    );
  }

  const url = `${SITE_URL}/${article.slug}`;
  const jsonLd = [
    buildArticleSchema({
      headline: article.heroTitle,
      description: article.metaDescription,
      url,
      datePublished: article.published,
      dateModified: article.modified,
    }),
    buildFAQSchema(article.faqs || []),
    buildBreadcrumbSchema([
      { name: 'Home', url: `${SITE_URL}/` },
      { name: 'Comparisons', url: `${SITE_URL}/` },
      { name: article.heroTitle, url },
    ]),
  ];

  return (
    <div className="min-h-screen bg-[#F7F8F5]" data-testid={`comparison-page-${slug}`}>
      <SEO
        title={article.title}
        description={article.metaDescription}
        canonical={url}
        keywords={article.keywords}
        jsonLd={jsonLd}
        ogType="article"
        articlePublished={article.published}
        articleModified={article.modified}
      />

      {/* Hero */}
      <section className="relative bg-[#0A0D0C] pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#004534]/40 to-transparent" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10 max-w-5xl">
          <nav className="mb-6 text-xs text-white/50 flex items-center gap-2" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#D4AF37] flex items-center gap-1"><Home className="h-3 w-3" /> Home</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-white/80 truncate">{article.heroTitle}</span>
          </nav>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#0A0D0C] text-xs font-bold uppercase tracking-wider mb-6">
              <GitCompare className="h-3.5 w-3.5" /> {article.heroKicker}
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 font-['Outfit'] leading-tight">
              {article.heroTitle}
            </h1>
            <p className="text-lg text-[#D4AF37] font-medium mb-4">{article.heroSubtitle}</p>
            <p className="text-white/70 max-w-3xl leading-relaxed">{article.heroSummary}</p>
          </motion.div>
        </div>
      </section>

      {/* Comparison Table */}
      {article.table && (
        <section className="py-14">
          <div className="container mx-auto px-6 lg:px-12 max-w-6xl">
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="comparison-table">
                  <thead className="bg-[#004534] text-white">
                    <tr>
                      {article.table.headers.map((h, i) => (
                        <th key={i} className={`px-4 py-4 text-left font-bold ${i === 0 ? 'sticky left-0 bg-[#004534] z-10' : ''}`}>
                          {i === 1 && article.table.headers.length <= 3 ? (
                            <span className="text-[#D4AF37]">{h} ★</span>
                          ) : (
                            h
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {article.table.rows.map((row, ri) => (
                      <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-[#F7F8F5]'}>
                        {row.map((cell, ci) => (
                          <td
                            key={ci}
                            className={`px-4 py-3 border-t border-gray-100 align-top ${
                              ci === 0 ? 'font-semibold text-[#004534] sticky left-0 bg-inherit' : 'text-gray-700'
                            } ${ci === 1 && article.table.headers.length <= 3 ? 'bg-[#D4AF37]/10 text-[#7A6621] font-semibold' : ''}`}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Sections */}
      <section className="py-14 bg-white border-y">
        <div className="container mx-auto px-6 lg:px-12 max-w-3xl">
          <div className="space-y-10">
            {article.sections.map((sec, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <h2 className="text-2xl md:text-3xl font-bold text-[#004534] mb-3 font-['Outfit'] flex items-start gap-3">
                  <span className="inline-block w-1 h-7 bg-[#D4AF37] mt-1.5 flex-shrink-0" />
                  {sec.heading}
                </h2>
                <p className="text-gray-700 leading-relaxed text-base whitespace-pre-line">{sec.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      {article.faqs && article.faqs.length > 0 && (
        <section className="py-14" data-testid="comparison-faqs">
          <div className="container mx-auto px-6 lg:px-12 max-w-3xl">
            <div className="flex items-center gap-3 mb-8">
              <HelpCircle className="h-6 w-6 text-[#D4AF37]" />
              <h2 className="text-3xl font-bold text-[#004534] font-['Outfit']">Frequently Asked Questions</h2>
            </div>
            <div className="space-y-4">
              {article.faqs.map((f, i) => (
                <details key={i} className="group bg-white rounded-xl border border-gray-200 p-5 hover:border-[#D4AF37]/40 transition-colors">
                  <summary className="cursor-pointer flex items-start justify-between gap-4 font-semibold text-[#004534] text-base list-none">
                    <span>{f.q}</span>
                    <ChevronRight className="h-5 w-5 flex-shrink-0 mt-0.5 group-open:rotate-90 transition-transform" />
                  </summary>
                  <p className="mt-3 text-gray-700 leading-relaxed text-sm">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Attribution */}
      <section className="py-8 bg-[#F7F8F5]">
        <div className="container mx-auto px-6 lg:px-12 max-w-3xl">
          <div className="bg-white border border-gray-200 rounded-2xl p-5 flex items-start gap-4">
            <Sparkles className="h-5 w-5 text-[#D4AF37] mt-1 flex-shrink-0" />
            <p className="text-sm text-gray-700 leading-relaxed">
              This comparison is compiled by <span className="font-semibold text-[#004534]">{BRAND}</span> — the manufacturer of Pre-Laminated 6H Acrylic Boards with CriLam PUR-Bond™ technology in Kanpur, UP.
              Third-party brand attributes are collected from publicly available product literature (2026). Please contact any brand directly for the latest specifications.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-gradient-to-br from-[#004534] to-[#0A0D0C] text-white">
        <div className="container mx-auto px-6 lg:px-12 max-w-3xl text-center">
          <h3 className="text-3xl md:text-4xl font-bold mb-4 font-['Outfit']">{article.cta?.title || 'See CriLam in Action'}</h3>
          <p className="text-white/70 mb-8">Design your interior with the only 6H Pre-Laminated Acrylic Board manufactured in Kanpur, UP.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to={article.cta?.link || '/ai-visualizer'} data-testid="comparison-cta-primary">
              <button className="px-8 py-4 bg-[#D4AF37] text-[#004534] font-bold rounded-lg hover:bg-white transition-colors flex items-center gap-2">
                {article.cta?.button || 'Try Design Studio'} <ArrowRight className="h-5 w-5" />
              </button>
            </Link>
            <Link to="/contact" data-testid="comparison-cta-contact">
              <button className="px-8 py-4 border-2 border-white/40 text-white font-medium rounded-lg hover:bg-white/10">
                Talk to CriLam <ChevronRight className="h-5 w-5 inline" />
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ComparisonPage;
